/**
 * <review-stories> — swipeable review cards.
 *
 * Swiping uses the browser's own scroll-snap. This element only marks the
 * card in view (.is-active), moves the progress dots and drives the desktop
 * arrows. Without it, every card still shows at full size and scrolls.
 */
class ReviewStories extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-stories-track]');
    this.cards = Array.from(this.querySelectorAll('[data-story]'));
    this.dots = Array.from(this.querySelectorAll('[data-story-dot]'));
    this.prev = this.querySelector('[data-stories-prev]');
    this.next = this.querySelector('[data-stories-next]');
    if (!this.track || this.cards.length === 0) return;

    this.index = 0;
    this.classList.add('is-enhanced');
    this.setActive(0);

    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.setActive(this.closestIndex());
      });
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.prev?.addEventListener('click', () => this.goTo(this.index - 1));
    this.next?.addEventListener('click', () => this.goTo(this.index + 1));
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.index + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.index - 1); }
    });
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  // The active card is the one whose left edge sits nearest the track's snap edge.
  closestIndex() {
    const start = this.track.getBoundingClientRect().left + parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart || 0);
    let best = 0;
    let bestDistance = Infinity;
    this.cards.forEach((card, i) => {
      const distance = Math.abs(card.getBoundingClientRect().left - start);
      if (distance < bestDistance) { bestDistance = distance; best = i; }
    });
    return best;
  }

  setActive(index) {
    this.index = Math.max(0, Math.min(index, this.cards.length - 1));
    this.cards.forEach((card, i) => card.classList.toggle('is-active', i === this.index));
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
    this.prev?.setAttribute('aria-disabled', String(this.index === 0));
    this.next?.setAttribute('aria-disabled', String(this.index === this.cards.length - 1));
  }

  goTo(index) {
    const target = Math.max(0, Math.min(index, this.cards.length - 1));
    const card = this.cards[target];
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const padding = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart || 0);
    this.track.scrollTo({ left: card.offsetLeft - this.track.offsetLeft - padding, behavior: reduce ? 'auto' : 'smooth' });
    this.setActive(target);
  }
}

if (!customElements.get('review-stories')) customElements.define('review-stories', ReviewStories);
